// Parse the pair URL the desktop hands the user. The desktop emits exactly
//   <httpOrigin>/pair#fp=<64-hex>
// — fingerprint in the fragment so it never reaches the relay's HTTP access
// log. We mirror that strictness here: any deviation is rejected with a
// human-readable error so a copy/paste mistake surfaces clearly instead of
// silently opening a WebSocket against a bogus fingerprint.

import { isValidFingerprint } from '@baton/remote-protocol';

export interface ParsedPairUrl {
  /** Lowercase 64-hex install fingerprint extracted from the fragment. */
  fingerprint: string;
  /** Origin of the relay (e.g. `https://remote.getbaton.dev`). */
  origin: string;
}

export type PairUrlParseError = 'missing-fragment' | 'missing-fp' | 'invalid-fingerprint';

export class PairUrlError extends Error {
  readonly code: PairUrlParseError;
  constructor(code: PairUrlParseError, message: string) {
    super(message);
    this.code = code;
  }
}

/**
 * Extract the install fingerprint from the current page URL's fragment.
 * Throws `PairUrlError` if the fragment is missing or malformed.
 */
export function parsePairUrl(href: string): ParsedPairUrl {
  const url = new URL(href);
  const fragment = url.hash.startsWith('#') ? url.hash.slice(1) : url.hash;
  if (!fragment) {
    throw new PairUrlError(
      'missing-fragment',
      'No fragment in URL — expected `#fp=<fingerprint>`.'
    );
  }
  // URLSearchParams handles `fp=…&extra=…` cleanly; we don't refuse extras
  // so a future protocol revision can carry additional fragment fields
  // without breaking older clients.
  const params = new URLSearchParams(fragment);
  const fp = params.get('fp');
  if (!fp) {
    throw new PairUrlError('missing-fp', 'URL fragment missing `fp` parameter.');
  }
  if (!isValidFingerprint(fp)) {
    throw new PairUrlError(
      'invalid-fingerprint',
      'Fingerprint must be 64 lowercase hex characters.'
    );
  }
  return { fingerprint: fp, origin: url.origin };
}

/**
 * Build the WebSocket URL for the client slot from the parsed pair URL.
 * `https://` → `wss://`, `http://` → `ws://`. The relay's slot path shape
 * is `/c/<fp>` (the desktop side opens `/d/<fp>`, which we never need here).
 */
export function clientSocketUrl(parsed: ParsedPairUrl): string {
  const wsOrigin = parsed.origin.replace(/^http/, 'ws');
  return `${wsOrigin}/c/${parsed.fingerprint}`;
}
